import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { applyEdgeChanges, applyNodeChanges, EdgeChange, NodeChange } from '@xyflow/react';

import { setEdges, setIsUpdate, setNodes } from '../store';

function deepEqual(a: any, b: any) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useSyncFlowChanges() {
  const dispatch = useAppDispatch();

  const nodes = useAppSelector(state => state.aiAgentReducer.nodes);
  const edges = useAppSelector(state => state.aiAgentReducer.edges);

  const prevNodesRef = useRef(nodes);
  const prevEdgesRef = useRef(edges);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const newNodes = applyNodeChanges(changes, nodes);

      const hasMeaningfulChange = changes.some(change => {
        if (change.type === 'add') return true;
        if (change.type === 'remove') return true;
        if (change.type === 'position' && change.position) return true;

        return false;
      });

      if (hasMeaningfulChange) {
        dispatch(setIsUpdate(true));
      }

      if (!deepEqual(prevNodesRef.current, newNodes)) {
        dispatch(setNodes(() => newNodes));
        prevNodesRef.current = newNodes;
      }
    },
    [dispatch, nodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const newEdges = applyEdgeChanges(changes, edges);

      if (!deepEqual(prevEdgesRef.current, newEdges)) {
        dispatch(setEdges(() => newEdges));
        prevEdgesRef.current = newEdges;
      }
    },
    [dispatch, edges]
  );

  const resetChanges = () => {
    prevNodesRef.current = nodes;
    prevEdgesRef.current = edges;
  };

  return {
    onNodesChange,
    onEdgesChange,
    resetChanges
  };
}
